import axios from 'axios';
import styles from './styles';

import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

import { getListItemTemplate } from './templates/getListItemTemplate';
import { getDetailsItemTemplate } from './templates/getDetailsItemTemplate';
import { calculateZoomWithClosestMarker } from './plugins/closest-marker';
import { onIdle, findMarkerById, arrayToLatLng } from './utils';
import { freeze } from '../../helpers/misc';

import MarkerIcon from '../../../images/icon-map-marker.svg';
import ActiveMarkerIcon from '../../../images/icon-map-active-marker.svg';
import ClusterIcon from '../../../images/icon-map-cluster-marker.svg';
import UserMarker from '../../../images/icon-map-user-marker.svg';

const Italy = [44.7752633, 10.7178536];

export class StoreLocator {
  constructor (target) {
    this.section = target;
    this.ref = {
      section: this.section,
      mapContainer: this.section.querySelector('.map'),
      form: this.section.querySelector('.map-form'),
      mapInput: this.section.querySelector('.map-input'),
      resultsList: this.section.querySelector('.results-list'),
      resultsDetails: this.section.querySelector('.result-details'),
      currentLocationBtn: this.section.querySelector('.use-current-location'),
    };

    this.defaultStoreImage = this.section.dataset.defaultStoreImage || '';
    this.followUsLabel = this.section.dataset.followUsLabel || 'SEGUICI';
    this.maxDistance = parseFloat(this.section.dataset.maxDistance) || 50; // Default 50km

    this.endpoints = {
      apiKey: '',
      shops: `${CMS.contextPath}/.rest/delivery/store-locator?orderBy=top%20desc,displayName%20asc`,
    };

    this.API_KEY = this.section.dataset.apiKey || '';
    this.response = null;

    this.minZoom = 2;
    this.maxZoom = 16;
    this.defaultZoom = 7;
    this.defaultLocation = arrayToLatLng(Italy);

    this.map = null;
    this.markers = [];
    this.filteredMarkers = [];
    this.userMarker = null;
    this.geocoder = null;
    this.autocomplete = null;
    this.isOpenning = false;
    this.activeMarker = null;
    this.previousMarker = null;
    this.currentPosition = null; // Store current user position for distance calculations
  }

  loading(isLoading = true) {
    this.section.classList.toggle('loading', isLoading);
  }

  async init () {
    await this.createMap();
    await this.getData();

    this.createMarkers();
    this.createMarkerCluster();

    this.loading(false);
    this.initAutocomplete();
    this.initGeocoder();
    this.renderList();
    this.getUserLocation();

    this.addMapListeners();
    this.onCurrentLocationClick();
    this.onInputChange();
    this.onSubmit();
    this.populateFieldsFromUrlParams();
  }

  async createMap () {
    const loader = new Loader({
      apiKey: this.API_KEY,
      version: '3.55',
      libraries: ['places', 'geometry'],
      language: document.documentElement.lang
    });

    const google = await loader.load();

    this.map = new google.maps.Map(this.ref.mapContainer, {
      center: this.defaultLocation,
      zoom: this.defaultZoom,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'cooperative',
      styles,
    });

    await onIdle(this.map);
  }

  async getData () {
    try {
      this.response = await axios(this.endpoints.shops);
      this.response = this.response?.data?.results || [];
    } catch (err) {
      this.response = [];
      console.warn(err);
    }
  }

  createMarkers () {
    this.markers = this.response.map(item => {
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lng);

      const marker = new google.maps.Marker({
        position: { lat, lng },
        info: item,
        icon: {
          url: MarkerIcon,
        },
        zIndex: 100,
      });

      this.onMarkerClick(marker);

      return marker
    });
    this.filteredMarkers = this.markers;
  }

  calculateDistances () {
    if (!this.currentPosition) {
      return;
    }

    this.markers.forEach(marker => {
      const markerPosition = new google.maps.LatLng(marker.info.lat, marker.info.lng);
      const computeDistanceBetween = google.maps.geometry.spherical.computeDistanceBetween(markerPosition, this.currentPosition);
      const km = computeDistanceBetween / 1000;
      marker.info.distance = km;
      
      // Debug logging
      console.log(`Marker ${marker.info.displayName || marker.info.name}: ${km.toFixed(2)} km`);
    });
  }

  onMarkerClick (marker) {
    marker.addListener('click', () => {
      this.isOpenning = true;
      this.activeMarker = marker;
      this.openMarker();
      this.isOpenning = false;
    });
  }

  openMarker () {
    if (this.previousMarker !== null) {
      this.previousMarker.setIcon(MarkerIcon);
    }
    this.previousMarker = this.activeMarker;

    this.activeMarker.setIcon(ActiveMarkerIcon);
    this.activeMarker.setZIndex(101);

    this.renderDetails();
    this.ref.resultsList.classList.add('hide');
    this.ref.resultsDetails.classList.remove('hide');

    this.map.setCenter(this.activeMarker.getPosition());
    this.map.setZoom(this.maxZoom);
  }

  createMarkerCluster () {
    this.markersCluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      algorithm: new GridAlgorithm({
        maxDistance: 60000,
        gridSize: 40,
      }),
      renderer: {
        render: ({ count, position }) => new google.maps.Marker({
          position,
          visible: Boolean(count),
          icon: {
            url: ClusterIcon,
            scaledSize: new google.maps.Size(40, 40),
          },
          label: {
            text: String(count),
            color: '#483627',
            fontSize: '18px',
            className: 'selleria-marker'
          },
          labelOrigin: new google.maps.Point(20, -20)
        })
      }
    })
  }

  initAutocomplete () {
    this.autocomplete = new google.maps.places.Autocomplete(this.ref.mapInput, {
      types: ['geocode']
    });

    this.autocomplete.bindTo('bounds', this.map);
    this.autocomplete.setFields(['place_id', 'geometry', 'name']);
  }

  initGeocoder () {
    this.geocoder = new google.maps.Geocoder();
  }

  renderList () {
    this.updateListHTML();
    this.onListClick();
  }

  getUserLocation () {
    navigator.geolocation.getCurrentPosition((location) => {
      const position = new google.maps.LatLng(location.coords.latitude, location.coords.longitude)
      const zoom = calculateZoomWithClosestMarker({
        position,
        maxZoom: this.maxZoom,
        mapContainer: this.ref.mapContainer,
        markers: this.markers
      });

      this.userCoordinates = location.coords;
      this.currentPosition = position;
      this.calculateDistances();
      this.createUserMarker(position);
      this.map.panTo(position);
      this.map.setZoom(zoom);
    }, console.warn); //eslint-disable-line
  }

  createUserMarker (position) {
    if (this.userMarker) {
      this.userMarker.setMap(null);
    }

    this.userMarker = new google.maps.Marker({
      position,
      icon: {
        url: UserMarker,
      },
      optimized: false,
      map: this.map,
    });
  }

  addMapListeners () {
    this.map.addListener('dragstart', () => {
      this.closeMarker();
    });

    this.map.addListener('zoom_changed', () => {
      if (this.isOpenning) {
        return;
      }

      this.closeMarker();
    });
  }

  onCurrentLocationClick () {
    this.ref.currentLocationBtn.addEventListener('click', () => {
      this.getUserLocation();
      this.ref.form.location.value = '';
    });
  }

  onInputChange () {
    this.ref.mapInput.addEventListener('keyup', () => {
      this.hideLocationError();
    });
  }

  onSubmit () {
    let prevBrand = '';

    this.ref.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (this.ref.form.brand.value !== prevBrand) {
        this.filterMarkersByBrand();
        this.renderList();
        prevBrand = this.ref.form.brand.value;
      }

      await this.setLocation();
    });
  }

  filterMarkersByBrand () {
    const currentBrand = this.ref.form.brand.value;

    this.filteredMarkers = this.markers.filter((marker) => {
      const poiIsEquipe = marker.info.equipe || false;
      const poiIsAmerigo = marker.info.amerigo || false;
      
      // Brand filtering
      let brandMatch = true;
      if (currentBrand === 'EQUIPE') {
        brandMatch = poiIsEquipe;
      } else if (currentBrand === 'AMERIGO') {
        brandMatch = poiIsAmerigo;
      }

      // Distance filtering - only apply if we have a current position
      let distanceMatch = true;
      if (this.currentPosition && marker.info.distance !== undefined) {
        distanceMatch = marker.info.distance <= this.maxDistance;
      }

      const shouldShow = brandMatch && distanceMatch;
      marker.setVisible(shouldShow);

      // Debug logging
      if (this.currentPosition) {
        console.log(`Filtering ${marker.info.displayName || marker.info.name}: brand=${brandMatch}, distance=${marker.info.distance?.toFixed(2)}km (max=${this.maxDistance}km), distanceMatch=${distanceMatch}, shouldShow=${shouldShow}`);
      }

      return shouldShow;
    });
  }

  populateFieldsFromUrlParams () {
    const params = (new URL(document.location)).searchParams;

    this.ref.form.brand.value = params.get('brand');
    this.ref.form.brand.SelectFx.setValue(params.get('brand'));

    this.ref.form.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  async setLocation () {
    if (!this.ref.mapInput.value) {
      return;
    }

    const results = await this.geocode();

    if (!results.length) {
      return;
    }

    const { types, geometry } = results[0];

    // Set current position for distance calculations
    this.currentPosition = geometry.location;
    this.calculateDistances();

    if (types.includes('locality') || types.includes('country')) {
      this.map.setCenter(geometry.location);
      this.map.fitBounds(geometry.viewport, 0);
      this.filterMarkersByBrand();
      this.renderList();
      return;
    }

    const zoom = calculateZoomWithClosestMarker({
      position: geometry.location,
      maxZoom: this.maxZoom,
      mapContainer: this.ref.mapContainer,
      markers: this.markers
    }) || this.maxZoom;

    this.map.setZoom(zoom);
    this.map.panTo(geometry.location);
    this.filterMarkersByBrand();
    this.renderList();
  }

  geocode () {
    return new Promise((resolve) => {
      const address = this.ref.mapInput.value;

      this.geocoder.geocode({ address }, (results, status) => {
        if (!results || status !== google.maps.GeocoderStatus.OK) {
          console.warn('Error while searching address');
          this.showLocationError();

          return;
        }

        resolve(results);
      });
    })
  }

  showLocationError () {
    const fieldWrapper = this.ref.mapInput.closest('.field-wrapper');
    const validationMessage = fieldWrapper.querySelector('.validation-message');

    fieldWrapper.classList.add('error');
    validationMessage.classList.remove('hide');
  }

  hideLocationError () {
    const fieldWrapper = this.ref.mapInput.closest('.field-wrapper');
    const validationMessage = fieldWrapper.querySelector('.validation-message');

    fieldWrapper.classList.remove('error');
    validationMessage.classList.add('hide');
  }

  closeMarker () {
    this.ref.resultsList.classList.remove('hide');

    this.ref.resultsDetails.classList.add('hide');
    this.ref.resultsList.classList.remove('hide');

    if (this.activeMarker) {
      this.activeMarker.setIcon(MarkerIcon);
      this.activeMarker.setZIndex(100);
      this.activeMarker = null;
    }
  }

  updateListHTML () {
    let html = '';

    if (this.filteredMarkers.length) {
      html = this.filteredMarkers.map(marker => {
        // Always regenerate HTML to include updated distance information
        marker.generatedListHtml = getListItemTemplate(marker);
        return marker.generatedListHtml;
      }).join('');
    }

    this.ref.resultsList.innerHTML = html;
  }

  onListClick () {
    const showButtons = this.ref.resultsList.querySelectorAll('[data-show-details]');

    showButtons.forEach(el => {
      el.addEventListener('click', (e) => {
        this.isOpenning = true;
        const id = e.currentTarget.dataset.showDetails;

        this.activeMarker = findMarkerById(id, this.markers);
        this.openMarker();
        this.isOpenning = false;
      });
    });
  }

  renderDetails () {
    this.updateDetailsHTML();
    this.onDetailsCloseClick();
  }

  updateDetailsHTML () {
    if (!this.activeMarker.generatedDetailsHtml) {
      this.activeMarker.generatedDetailsHtml = getDetailsItemTemplate(
        this.activeMarker,
        this.userCoordinates,
        this.followUsLabel,
        this.defaultStoreImage
      );
    }

    this.ref.resultsDetails.innerHTML = this.activeMarker.generatedDetailsHtml;
  }

  onDetailsCloseClick () {
    const closeBtn = this.ref.resultsDetails.querySelector('[data-close-details]');

    closeBtn.addEventListener('click', async (e) => {
      e.preventDefault();

      this.closeMarker();
      await freeze(100);
    });
  }
}
